import cloneDeep from "lodash-es/cloneDeep";
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { NavigationType, useLocation, useNavigationType, useSearchParams } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import {
  URL_FILTER_KEYS_KEY,
  URL_FILTER_TAB_KEY,
  URL_FILTER_TYPES_KEY,
  URL_FILTER_VALUES_KEY,
  URL_SEARCH_KEY,
  URL_SORT_DIRECTION,
  URL_SORT_KEY,
  URL_VIEW_KEY,
} from "constants/urlQueryKeys";
import useTypedContext from "hooks/useTypedContext";
import { SearchQueryOrderDirection } from "types/generated";

import {
  getInitialFiltersMap,
  getSessionStorageKeys,
  recalculateFilters,
  removeFiltersFromStorage,
  setUpdatedFiltersUrlParams,
  syncFiltersOrder,
} from "./helpers";
import { useEmptyDefaultView } from "./hooks";
import {
  ActiveFilter,
  ActiveFiltersMap,
  FilterItem,
  FilterItemSettings,
  FiltersDictionary,
  FiltersItemsOptionsMap,
  SavedFilterView,
  SortOption,
} from "./types";
import { useDefaultView } from "./useDefaultView";
import useMakeApplyFiltersContext from "./useMakeApplyFiltersContext";
import usePermalinkView from "./usePermalinkView";

export type FiltersContextProps = {
  allFilters: FilterItem[];
  filters: FilterItem[];
  filtersItemsOptionsMap: FiltersItemsOptionsMap;
  sortOptions: SortOption[];
  defaultEmptyFilterView: SavedFilterView;
  initialSortOption: string | null;
  initialSortDirection: SearchQueryOrderDirection | null;
  activeFilters: ActiveFiltersMap;
  activeFiltersByFilterName: ActiveFiltersMap;
  setActiveFilter: (filter: ActiveFilter) => void;
  deleteActiveFilter: (filter: ActiveFilter) => void;
  resetAllFilters: () => void;
  setGenericFilter: (newFilter: FilterItem) => FilterItem[];
  setActiveFilters: (filters: ActiveFiltersMap) => void;
  setActiveFiltersFromInitialParams: () => void;
  filtersLoading: boolean;
  forceReMountSidebar: () => void;
  forceReMountLabelGroups: () => void;
  reMountSidebar: boolean;
  reMountLabelGroups: boolean;
  toggleNewLabelGroup: (value: boolean) => void;
  isNewLabelGroupOpen: boolean;
  setOpenSections: (filterName: string, value: boolean) => void;
  hasOpenSections: boolean;
  setShouldExpandSections: (value: boolean) => void;
  shouldExpandSections: boolean | undefined;
  filtersDictionary?: FiltersDictionary;
  filtersOrderSettingsKey: string;
  filtersOrder: FilterItemSettings[];
  filterType: string;
  currentView?: SavedFilterView;
  setCurrentView: (view?: SavedFilterView) => void;
  setFiltersOrder: Dispatch<SetStateAction<FilterItemSettings[]>>;
  filtersRevealedOnHover: boolean;
  setFiltersRevealedOnHover: (revealed: boolean) => void;
  resetOpenSections: () => void;
  disabledFilters?: string[];
  enableViews: boolean;
};

type ApplyFiltersContextProps = {
  applyFilter: (
    filterName: string,
    searchSuggestionsDictionary: FiltersDictionary
  ) => (value: string) => void;
  applyLabelFilter: (filterName: string) => (value: string) => void;
  applySpaceFilter: (
    filterName: string,
    searchSuggestionsDictionary: FiltersDictionary
  ) => (value: string) => void;
};

export const FiltersContext = createContext<FiltersContextProps | undefined>(undefined);
FiltersContext.displayName = "FiltersContext";

export const ApplyFiltersContext = createContext<ApplyFiltersContextProps | undefined>(undefined);
ApplyFiltersContext.displayName = "ApplyFiltersContext";

type BaseFiltersProps = {
  filters: FilterItem[];
  filtersItemsOptionsMap: FiltersItemsOptionsMap;
  /**
   * deprecatedFilterNames:
   * filters that will be hidden by default unless used by a view or url param
   */
  deprecatedFilterNames?: string[];
  sortOptions: SortOption[];
  initialSortOption: string | null;
  initialSortDirection: SearchQueryOrderDirection | null;
  filtersLoading: boolean;
  filtersDictionary?: FiltersDictionary;
  pollActiveSections?: (sections: string[]) => void;
  filtersOrderSettingsKey: string;
  children: React.ReactNode;
  filtersType: string;
  applyFilterCallback?: () => void;
  disabledFilters?: string[];
  resetFiltersCallback?: () => void;
};

type OnlyFiltersProps = BaseFiltersProps & {
  currentSavedView?: never;
  setCurrentSavedView?: never;
  disableViews: true;
};

type FiltersAndViewsProps = BaseFiltersProps & {
  currentSavedView?: SavedFilterView;
  setCurrentSavedView: (view?: SavedFilterView) => void;
  disableViews?: false;
};

export type FiltersProps = OnlyFiltersProps | FiltersAndViewsProps;

function Filters(props: OnlyFiltersProps): React.JSX.Element;
function Filters(props: FiltersAndViewsProps): React.JSX.Element;
function Filters(props: OnlyFiltersProps | FiltersAndViewsProps) {
  const {
    children,
    deprecatedFilterNames = [],
    initialSortOption,
    initialSortDirection,
    filters,
    sortOptions,
    filtersLoading,
    filtersItemsOptionsMap,
    filtersDictionary,
    pollActiveSections,
    filtersOrderSettingsKey,
    filtersType,
    currentSavedView,
    setCurrentSavedView,
    applyFilterCallback,
    disabledFilters,
    resetFiltersCallback,
    disableViews,
  } = props;
  const storage = sessionStorage;
  const enableViews = !disableViews;
  const [filtersOrder, setFiltersOrder] = useState<FilterItemSettings[]>([]);

  const defaultEmptyFilterView = useEmptyDefaultView(
    filtersOrder,
    initialSortDirection,
    initialSortOption,
    deprecatedFilterNames
  );

  const {
    storageUrlFilterKeysKey,
    storageUrlFilterTypesKey,
    storageUrlFilterValuesKey,
    storageUrlSearchKey,
    storageUrlSortDirection,
    storageUrlSortKey,
  } = getSessionStorageKeys(filtersOrderSettingsKey);
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const [cachedFilters, updateCachedFilters] = useState(() => filters);
  const [activeFilters, setActiveFilters] = useState<ActiveFiltersMap>(new Map());
  const [genericFilters, setGenericFilters] = useState<FilterItem[]>([]);

  const [reMountSidebar, forceReMountSidebar] = useState(false);
  const [reMountLabelGroups, forceReMountLabelGroups] = useState(false);
  const [openSections, setOpenSection] = useState<string[]>([]);
  const [shouldExpandSections, setShouldExpandSections] = useState<boolean | undefined>(undefined);

  const resetOpenSections = useCallback(() => {
    setOpenSection([]);
    isNewLabelGroupOpen.current = false;
  }, []);

  const isNewLabelGroupOpen = useRef(false);
  const defaultView = useDefaultView(filtersOrder, filtersType, defaultEmptyFilterView);

  const { reportError } = useTypedContext(FlashContext);
  const viewIdParam = searchParams.get(URL_VIEW_KEY) ?? undefined;
  const hasViewPermalink = Boolean(enableViews && viewIdParam);
  const permalinkView = usePermalinkView(viewIdParam, hasViewPermalink, defaultEmptyFilterView);

  const handleUpdateUrlParamsForDefaultView = useCallback(
    (
      option: string | null,
      direction: SearchQueryOrderDirection | null,
      filters: ActiveFiltersMap,
      search: string | null
    ) => {
      const updatedFiltersParams = setUpdatedFiltersUrlParams(
        filters,
        searchParams,
        storage,
        filtersOrderSettingsKey
      );

      if (updatedFiltersParams.has(URL_FILTER_TAB_KEY)) {
        updatedFiltersParams.delete(URL_FILTER_TAB_KEY);
      }

      if (option) {
        updatedFiltersParams.set(URL_SORT_KEY, encodeURIComponent(option));
        storage.setItem(storageUrlSortKey, encodeURIComponent(option));
      } else {
        updatedFiltersParams.delete(URL_SORT_KEY);
        storage.removeItem(storageUrlSortKey);
      }

      if (direction) {
        updatedFiltersParams.set(URL_SORT_DIRECTION, encodeURIComponent(direction));
        storage.setItem(storageUrlSortDirection, encodeURIComponent(direction));
      } else {
        updatedFiltersParams.delete(URL_SORT_DIRECTION);
        storage.removeItem(storageUrlSortDirection);
      }

      if (search) {
        try {
          updatedFiltersParams.set(URL_SEARCH_KEY, search);
          storage.setItem(storageUrlSearchKey, btoa(encodeURIComponent(JSON.stringify(search))));
        } catch {
          // search is malformed, remove it
          updatedFiltersParams.delete(URL_SEARCH_KEY);
          storage.removeItem(storageUrlSearchKey);
        }
      } else {
        updatedFiltersParams.delete(URL_SEARCH_KEY);
        storage.removeItem(storageUrlSearchKey);
      }

      setSearchParams(updatedFiltersParams, { replace: true });
    },
    [
      filtersOrderSettingsKey,
      storage,
      storageUrlSearchKey,
      storageUrlSortDirection,
      storageUrlSortKey,
      searchParams,
      setSearchParams,
    ]
  );

  const sortedFilters = useMemo(() => {
    // skip duplicates
    const arrayUniqueByKey = [
      ...new Map([...cachedFilters, ...genericFilters].map((item) => [item.key, item])).values(),
    ];

    return arrayUniqueByKey.sort((a, b) => {
      if (a.key.startsWith("label") && b.key.startsWith("label")) {
        const aIndex = Number(a.key.replace("label", ""));
        const bIndex = Number(b.key.replace("label", ""));

        if (aIndex === bIndex) {
          return 0;
        }

        return aIndex > bIndex ? 1 : -1;
      }
      return a.key.localeCompare(b.key);
    });
  }, [cachedFilters, genericFilters]);

  const setActiveFiltersFromInitialParams = useCallback(() => {
    const [initialActiveFilters, initialGenericFilters] = getInitialFiltersMap(
      searchParams,
      storage,
      filtersOrderSettingsKey,
      filtersDictionary
    );

    setActiveFilters(initialActiveFilters);
    setGenericFilters(initialGenericFilters);

    if (searchParams.has(URL_FILTER_KEYS_KEY)) {
      storage.setItem(storageUrlFilterKeysKey, searchParams.get(URL_FILTER_KEYS_KEY) as string);
    }

    if (searchParams.has(URL_FILTER_TYPES_KEY)) {
      storage.setItem(storageUrlFilterTypesKey, searchParams.get(URL_FILTER_TYPES_KEY) as string);
    }

    if (searchParams.has(URL_FILTER_VALUES_KEY)) {
      storage.setItem(storageUrlFilterValuesKey, searchParams.get(URL_FILTER_VALUES_KEY) as string);
    }

    return {
      initialGenericFilters,
    };
  }, [
    filtersDictionary,
    filtersOrderSettingsKey,
    storage,
    storageUrlFilterKeysKey,
    storageUrlFilterTypesKey,
    storageUrlFilterValuesKey,
    searchParams,
  ]);

  const handleSetActiveFilters = useCallback((filters: ActiveFiltersMap) => {
    const [newActiveFilters, newGenericFilters] = recalculateFilters(filters);

    setActiveFilters(newActiveFilters);
    setGenericFilters(newGenericFilters);
  }, []);

  // Use default view as the initial one
  useEffect(() => {
    // Set default view when no current view or someone moved directly to empty stacks via routing
    if ((!currentSavedView || !location.search) && defaultView.data?.id) {
      if (hasViewPermalink) {
        // permalink resolution is still in flight - wait rather than falling through
        // to the default view below, which would apply the wrong view and rewrite the URL
        if (permalinkView.loading) {
          return;
        }

        if (permalinkView.data) {
          if (enableViews) {
            setCurrentSavedView(permalinkView.data);
          }
          setFiltersOrder(syncFiltersOrder(sortedFilters, permalinkView.data.order));
          handleSetActiveFilters(permalinkView.data.filters);
          // consume the id into the ordinary filter-snapshot URL form: the Stacks list
          // query reads filters straight off the URL, not this context, and leaving
          // ?view=<id> in place would re-resolve and re-clobber state on the next reload
          searchParams.delete(URL_VIEW_KEY);
          handleUpdateUrlParamsForDefaultView(
            permalinkView.data.sortOption,
            permalinkView.data.sortDirection,
            permalinkView.data.filters,
            permalinkView.data.search
          );
          return;
        }

        if (permalinkView.notFound) {
          reportError({ message: "This view is unavailable." });
          // drop the dead id so it doesn't ride along on every URL update from here on
          searchParams.delete(URL_VIEW_KEY);
          setSearchParams(searchParams, { replace: true });
          return;
        }
      }

      if (enableViews) {
        setCurrentSavedView(defaultView.data);
      }
      const syncedFilterOrder = syncFiltersOrder(sortedFilters, defaultView.data.order);

      const hasFilterParams =
        searchParams.has(URL_FILTER_KEYS_KEY) &&
        searchParams.has(URL_FILTER_TYPES_KEY) &&
        searchParams.has(URL_FILTER_VALUES_KEY);

      const hasSortParams = searchParams.has(URL_SORT_DIRECTION) && searchParams.has(URL_SORT_KEY);

      const hasQueryParam = searchParams.has(URL_SEARCH_KEY);

      if (hasFilterParams || hasSortParams || hasQueryParam) {
        const { initialGenericFilters } = setActiveFiltersFromInitialParams();

        const { sortOption, sortDirection } = defaultView.data;
        if (!hasSortParams && sortOption && sortDirection) {
          searchParams.set(URL_SORT_KEY, encodeURIComponent(sortOption));
          searchParams.set(URL_SORT_DIRECTION, encodeURIComponent(sortDirection));
          storage.setItem(storageUrlSortKey, encodeURIComponent(sortOption));
          storage.setItem(storageUrlSortDirection, encodeURIComponent(sortDirection));
          setSearchParams(searchParams, { replace: true });
        }

        // Enable filters when we activate them
        setFiltersOrder(
          syncedFilterOrder.map((item) => ({
            ...item,
            visible:
              initialGenericFilters.some(({ filterName }) => filterName == item.name) ||
              item.visible,
          }))
        );
      } else {
        setFiltersOrder(syncedFilterOrder);
        handleSetActiveFilters(defaultView.data.filters);
        handleUpdateUrlParamsForDefaultView(
          defaultView.data.sortOption,
          defaultView.data.sortDirection,
          defaultView.data.filters,
          defaultView.data.search
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    defaultView?.data?.id,
    location.search,
    hasViewPermalink,
    permalinkView.loading,
    permalinkView.data?.id,
    permalinkView.notFound,
  ]);

  const toggleNewLabelGroup = useCallback((value: boolean) => {
    // eslint-disable-next-line react-hooks/immutability
    isNewLabelGroupOpen.current = value;
  }, []);

  const hasOpenSections = useMemo(() => {
    // eslint-disable-next-line react-hooks/set-state-in-render
    setShouldExpandSections(undefined);

    return openSections.length > 0;
  }, [openSections]);

  const setOpenSections = useCallback(
    (filterName: string, value: boolean) => {
      if (value) {
        setOpenSection([...openSections, filterName]);
      } else {
        const newOpenSections = [...openSections];

        const indexToRemove = newOpenSections.findIndex(
          (openSection) => filterName === openSection
        );

        if (indexToRemove > -1) {
          newOpenSections.splice(indexToRemove, 1);
          setOpenSection(newOpenSections);
        }
      }
    },
    [openSections]
  );

  const orderedFilters = useMemo(() => {
    return filtersOrder.reduce<FilterItem[]>((acc, filter) => {
      const item = sortedFilters.filter(
        (item) => item.filterName === filter.name && filter.visible
      );

      if (item.length > 0) {
        acc.push(...item);
      }

      return acc;
    }, []);
  }, [filtersOrder, sortedFilters]);

  const activeFiltersByFilterName = useMemo(() => {
    return new Map([...activeFilters.values()].map((filter) => [filter.filterName, filter]));
  }, [activeFilters]);

  const handleForceReMountLabelGroups = useCallback(
    () => forceReMountLabelGroups(!reMountLabelGroups),
    [reMountLabelGroups]
  );

  const handleUpdateUrlParams = useCallback(
    (activeFilters: ActiveFiltersMap, replaceHistory = false) => {
      const updatedParams = setUpdatedFiltersUrlParams(
        activeFilters,
        searchParams,
        storage,
        filtersOrderSettingsKey
      );

      setSearchParams(updatedParams, { replace: replaceHistory });
    },
    [filtersOrderSettingsKey, storage, searchParams, setSearchParams]
  );

  const handleSetActiveFilter = useCallback(
    (filter: ActiveFilter) => {
      const copiedActiveFilters = cloneDeep(activeFilters);
      if (filter.values.length === 0 && copiedActiveFilters.has(filter.key)) {
        copiedActiveFilters.delete(filter.key);
      } else if (filter.values.length > 0) {
        copiedActiveFilters.set(filter.key, filter);
      } else {
        // no need to do anything since the filter has no values
        return;
      }

      // on generic filters being empty, we need to recalculate filters
      if (filter.filterName === "label" && filter.values.length === 0) {
        const [newActiveFilters, newGenericFilters] = recalculateFilters(copiedActiveFilters);
        setActiveFilters(newActiveFilters);
        setGenericFilters(newGenericFilters);
        handleUpdateUrlParams(newActiveFilters);
        handleForceReMountLabelGroups();
      }
      // default behavior
      else {
        setActiveFilters(copiedActiveFilters);
        handleUpdateUrlParams(copiedActiveFilters);
      }

      applyFilterCallback?.();
    },
    [activeFilters, applyFilterCallback, handleForceReMountLabelGroups, handleUpdateUrlParams]
  );

  const handleDeleteActiveFilter = useCallback(
    (filter: ActiveFilter) => {
      const copiedActiveFilters = cloneDeep(activeFilters);
      copiedActiveFilters.delete(filter.key);

      const [newActiveFilters, newGenericFilters] = recalculateFilters(copiedActiveFilters);

      setActiveFilters(newActiveFilters);
      handleUpdateUrlParams(newActiveFilters);
      setGenericFilters(newGenericFilters);
      applyFilterCallback?.();
    },
    [activeFilters, handleUpdateUrlParams, applyFilterCallback]
  );

  const handleAddGenericFilter = useCallback(
    (newFilter: FilterItem) => {
      const updatedFilters = [...genericFilters, newFilter];
      setGenericFilters(updatedFilters);

      return updatedFilters;
    },
    [genericFilters]
  );

  const clearStorage = useCallback(() => {
    removeFiltersFromStorage(storage, filtersOrderSettingsKey);
    storage.removeItem(storageUrlSortKey);
    storage.removeItem(storageUrlSortDirection);
    storage.removeItem(storageUrlSearchKey);
  }, [
    filtersOrderSettingsKey,
    storage,
    storageUrlSearchKey,
    storageUrlSortDirection,
    storageUrlSortKey,
  ]);

  const handleForceReMountSidebar = useCallback(() => {
    forceReMountSidebar(!reMountSidebar);
    setOpenSection([]);
    setShouldExpandSections(false);
  }, [reMountSidebar]);

  const handleResetAllFilters = useCallback(() => {
    setActiveFilters(new Map());
    handleUpdateUrlParams(new Map());
    setGenericFilters([]);
    handleForceReMountSidebar();
    resetFiltersCallback?.();
  }, [handleForceReMountSidebar, handleUpdateUrlParams, resetFiltersCallback]);

  useEffect(() => {
    /**
     * TODO: react-router migration - this should useNavigationType, however for some reason it's not updating from POP, let's come back to this on the migration cleanup
     * One seemingly related issue is here: https://github.com/remix-run/react-router/discussions/10053, but we don't pass the location as prop
     * If you use useNavigationType make sure that changing views in the filters works properly
     */
    if (navigationType === NavigationType.Pop && enableViews) {
      setCurrentSavedView(undefined);
    }

    // sync active filters with url params on history back/forward events!
    if (navigationType === NavigationType.Pop && activeFilters.size > 0) {
      setActiveFiltersFromInitialParams();
      handleForceReMountSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // remove storage data on Browser "reload" event
  useEffect(() => {
    //on browser page reload event
    window.addEventListener("beforeunload", clearStorage);

    return () => {
      window.removeEventListener("beforeunload", clearStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sync Filters settings
  useEffect(() => {
    setFiltersOrder(syncFiltersOrder(sortedFilters, filtersOrder));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedFilters]);

  useEffect(() => {
    if (!filtersLoading) {
      updateCachedFilters(filters);
    }
  }, [filters, filtersLoading]);

  // polling with expanded filters for performance optimization
  useEffect(() => {
    if (pollActiveSections) {
      const uniqFilterKeys = [...new Set(openSections)].filter((sectionKey) =>
        orderedFilters.find(({ filterName }) => sectionKey === filterName)
      );

      pollActiveSections(uniqFilterKeys);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSections]);

  // TODO: create separate context or hook for collapsed filters during next step or refactor
  const [filtersRevealedOnHover, setFiltersRevealedOnHover] = useState(false);

  const savedViewsLoading = filtersLoading || defaultView.loading;

  const value = useMemo(
    () => ({
      allFilters: sortedFilters,
      filters: orderedFilters,
      filtersOrder: filtersOrder,
      filtersItemsOptionsMap,
      sortOptions,
      defaultEmptyFilterView,
      initialSortOption,
      initialSortDirection,
      activeFilters,
      activeFiltersByFilterName,
      setActiveFilter: handleSetActiveFilter,
      deleteActiveFilter: handleDeleteActiveFilter,
      resetAllFilters: handleResetAllFilters,
      setGenericFilter: handleAddGenericFilter,
      setActiveFilters: handleSetActiveFilters,
      setFiltersOrder,
      filtersLoading: savedViewsLoading,
      forceReMountSidebar: handleForceReMountSidebar,
      forceReMountLabelGroups: handleForceReMountLabelGroups,
      reMountSidebar,
      reMountLabelGroups,
      toggleNewLabelGroup,
      isNewLabelGroupOpen: isNewLabelGroupOpen.current,
      hasOpenSections,
      setOpenSections,
      shouldExpandSections,
      setShouldExpandSections,
      setActiveFiltersFromInitialParams,
      filtersDictionary,
      filtersOrderSettingsKey,
      filterType: filtersType,
      currentView: currentSavedView,
      resetOpenSections,
      setCurrentView: (view?: SavedFilterView) => {
        setCurrentSavedView?.(view);

        if (view?.order) {
          setFiltersOrder(view.order);
        }
      },
      filtersRevealedOnHover,
      setFiltersRevealedOnHover,
      disabledFilters,
      enableViews,
    }),
    [
      activeFilters,
      activeFiltersByFilterName,
      currentSavedView,
      defaultEmptyFilterView,
      filtersDictionary,
      filtersItemsOptionsMap,
      filtersOrder,
      filtersOrderSettingsKey,
      filtersRevealedOnHover,
      setActiveFiltersFromInitialParams,
      filtersType,
      handleAddGenericFilter,
      handleDeleteActiveFilter,
      handleForceReMountLabelGroups,
      handleForceReMountSidebar,
      handleResetAllFilters,
      handleSetActiveFilter,
      handleSetActiveFilters,
      hasOpenSections,
      initialSortDirection,
      initialSortOption,
      orderedFilters,
      reMountLabelGroups,
      reMountSidebar,
      resetOpenSections,
      savedViewsLoading,
      setCurrentSavedView,
      setOpenSections,
      shouldExpandSections,
      sortOptions,
      sortedFilters,
      toggleNewLabelGroup,
      disabledFilters,
      enableViews,
    ]
  );

  const applyFiltersValue = useMakeApplyFiltersContext({
    allFilters: sortedFilters,
    setActiveFilter: handleSetActiveFilter,
    currentFilters: filters,
    activeFiltersByFilterName,
    setGenericFilter: handleAddGenericFilter,
    setFiltersOrder,
    filtersItemsOptionsMap,
  });

  return (
    <FiltersContext.Provider value={value}>
      <ApplyFiltersContext.Provider value={applyFiltersValue}>
        {children}
      </ApplyFiltersContext.Provider>
    </FiltersContext.Provider>
  );
}

export default Filters;
